<template>
  <v-navigation-drawer
    app
    v-model="$store.state.layout.drawer"
    class="primary"
    dark
    id="app-drawer"
  >
    <v-sheet
      color="white"
      height="58"
      class="d-flex justify-center align-center"
    >
      <v-img contain :src="brandConfig.logos.inline" height="38" />
      <!-- <v-img
        v-if="isStoreSync"
        src="@/assets/logo/StoreSyncPOSLineLogo.png"
        alt="StoreSyncPOS Logo"
        contain
        height="22"
      />
      <v-img
        v-else
        src="@/assets/logo/autoRepairLoogo.png"
        alt="AutoRepairDesk Logo"
        contain
        height="38"
      /> -->
    </v-sheet>

    <!-- 🔹 Navigation -->
    <v-list dense nav>
      <template v-for="(item, index) in items">
        <v-subheader v-if="item.header" :key="index">
          <v-row no-gutters class="align-center">
            <v-col>{{ item.header }}</v-col>
            <v-spacer />
            <v-col cols="auto" class="grey--text text--lighten-1 text-caption">
              {{ version }}
            </v-col>
          </v-row>
        </v-subheader>

        <!-- ▪ Divider -->
        <v-divider v-else-if="item.divider" />

        <!-- ▪ List Group (with children) -->
        <v-list-group
          v-else-if="item.items"
          :prepend-icon="item.icon"
          no-action
        >
          <template v-slot:activator>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <!-- ▪ Sub-items -->
          <template v-for="(subItem, i) in item.items">
            <v-list-group
              :key="i"
              v-if="subItem.items"
              sub-group
              :prepend-icon="subItem.icon"
            >
              <template v-slot:activator>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{ subItem.title }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list-group>

            <!-- ▪ Sub-item (leaf) -->
            <v-list-item
              v-else
              :to="!subItem.href ? { path: subItem.path } : null"
              :href="subItem.href"
              :target="subItem.target"
              :disabled="subItem.disabled"
              rel="noopener"
            >
              <v-list-item-content>
                <v-list-item-title>{{ subItem.title }}</v-list-item-title>
              </v-list-item-content>

              <v-chip
                v-if="subItem.badge"
                small
                label
                class="white--text pa-0 v-chip--x-small"
                :color="subItem.color || 'primary'"
              >
                {{ subItem.badge }}
              </v-chip>

              <v-list-item-action v-if="subItem.action">
                <v-icon :class="[subItem.actionClass || 'success--text']">
                  {{ subItem.action }}
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list-group>

        <!-- ▪ Single Item -->
        <v-list-item
          v-else
          :to="!item.href ? { path: item.path } : null"
          :href="item.href"
          :disabled="item.disabled"
          :target="item.target"
          rel="noopener"
        >
          <v-list-item-action v-if="item.icon">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-action>

          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>

          <v-chip
            v-if="item.badge"
            small
            label
            class="white--text pa-0 v-chip--x-small"
            :color="item.color || 'primary'"
          >
            {{ item.badge }}
          </v-chip>

          <v-list-item-action v-if="item.subAction">
            <v-icon class="success--text">{{ item.subAction }}</v-icon>
          </v-list-item-action>

          <v-chip
            v-else-if="item.chip"
            small
            label
            class="caption blue lighten-2 white--text mx-0"
          >
            {{ item.chip }}
          </v-chip>
        </v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from "vuex";
const pjson = require("../../../../package.json");

export default {
  name: "AppDrawer",
  data() {
    return {
      isStoreSync: false,
      items: [],
    };
  },
  computed: {
    ...mapGetters("integration", ["modules"]),
    ...mapGetters("brand", ["currentBrand", "brandConfig"]),
    version() {
      return pjson.version;
    },
  },
  mounted() {
    this.detectBrand();
    this.loadNav();
  },
  methods: {
    detectBrand() {
      const host = window.location.hostname.toLowerCase();
      if (host.includes("storesyncpos")) this.isStoreSync = true;
      else if (host.includes("autorepairdesk")) this.isStoreSync = false;
      else this.isStoreSync = true; // default
    },

    loadNav() {
      this.items = [
        { header: "Back Office" },
        {
          title: "Home",
          icon: "mdi-home",
          path: "/",
        },
        { divider: true },
        {
          title: this.$t("sideBar.locations"),
          icon: "mdi-store",
          path: "/location",
        },

        {
          title: this.$t("sideBar.contacts"),
          icon: "mdi-account",
          path: "/contact",
        },
        {
          title: this.$t("sideBar.inventory"),
          icon: "mdi-barcode-scan",
          items: [
            {
              title: this.$t("sideBar.product"),
              path: "/inventory/product",
            },
            {
              title: this.$t("sideBar.category"),
              path: "/inventory/category",
            },
            {
              title: "Item Transfer",
              path: "/inventory/item-transfer",
            },
          ],
        },
        {
          title: this.$t("sideBar.employees"),
          icon: "mdi-account-group",
          items: [
            {
              title: this.$t("sideBar.manage"),
              path: "/employee/list",
            },
            {
              title: this.$t("sideBar.locationRoles"),
              path: "/employee/role",
            },
          ],
        },
        {
          title: this.$t("sideBar.reviewInvoice"),
          icon: "mdi-receipt",
          path: "/invoice",
        },
        {
          title: this.$t("sideBar.marketplace"),
          icon: "mdi-apps",
          path: "/marketplace",
        },
        {
          title: this.$t("sideBar.reports"),
          icon: "mdi-file-chart",
          path: "/reports",
        },
        {
          title: this.$t("sideBar.settings"),
          icon: "mdi-settings",
          path: "/settings",
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/variable.scss";

.v-chip--x-small {
  font-size: 10px;
  height: 16px;
}
.v-chip--x-small .v-chip__content {
  line-height: 1;
  padding: 4px;
}
</style>